import { useState, useEffect } from 'react';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { ResponsiveTabsList, ResponsiveTabsTrigger } from '@/components/admin/ResponsiveTabsComponents';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useOrganizationContext } from '@/context/organization-provider';
import { Building2, Users, Settings, BarChart3, Package, UserCheck } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNavigate } from 'react-router-dom';
import { AnalyticsDashboard } from '@/components/admin/organization/AnalyticsDashboard';
import { OrganizationSettings } from '@/components/admin/organization/OrganizationSettings';
import { MemberManagement } from '@/components/admin/organization/MemberManagement';
import { PendingMembershipsManager } from '@/components/admin/organization/PendingMembershipsManager';
import OrganizationParcelTypesManager from '@/components/admin/organization/OrganizationParcelTypesManager';
import ParcelTypesErrorBoundary from '@/components/admin/ParcelTypesErrorBoundary';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import { usePendingMemberships } from '@/hooks/usePendingMemberships';
import { Badge } from '@/components/ui/badge';
const OrganizationAdmin = () => {
  const navigate = useNavigate();
  const { roles, isLoading: rolesLoading } = useUserRoles();
  const {
    organization
  } = useOrganizationContext();
  const { pendingMemberships } = usePendingMemberships();
  const [activeTab, setActiveTab] = useState('overview');
  
  console.log('[ORG-ADMIN-PAGE] Rendering with:', {
    roles: {
      platformRole: roles.platformRole,
      organizationRole: roles.organizationRole,
      isPlatformAdmin: roles.isPlatformAdmin,
      isOrgAdmin: roles.isOrgAdmin,
      hasAccessToAllOrgs: roles.hasAccessToAllOrgs
    },
    organization: !!organization,
    orgId: organization?.id,
    orgName: organization?.name,
    pendingMemberships: pendingMemberships.length
  });
  
  // Count pending requests for badge
  const pendingCount = pendingMemberships.filter(m => m.status === 'pending').length;

  const orgTabs = [
    { value: "overview", label: "Overview", icon: BarChart3 },
    { value: "members", label: "Members", icon: Users },
    { 
      value: "pending", 
      label: "Pending Requests", 
      icon: UserCheck,
      badge: pendingCount > 0 ? pendingCount : undefined
    },
    { value: "parcels", label: "Parcel Types", icon: Package },
    { value: "settings", label: "Settings", icon: Settings }
  ];

  // Redirect if not admin (but only after roles are loaded)
  useEffect(() => {
    console.log('[ORG-ADMIN-PAGE] Access check in useEffect:', {
      rolesLoading,
      isPlatformAdmin: roles.isPlatformAdmin,
      isOrgAdmin: roles.isOrgAdmin,
      shouldRedirect: !rolesLoading && !roles.isPlatformAdmin && !roles.isOrgAdmin
    });
    
    // Only redirect if roles are loaded AND user doesn't have access
    if (!rolesLoading && !roles.isPlatformAdmin && !roles.isOrgAdmin) {
      console.log('[ORG-ADMIN-PAGE] Redirecting due to insufficient permissions');
      navigate('/');
    }
  }, [rolesLoading, roles.isPlatformAdmin, roles.isOrgAdmin, navigate]);

  // Show loading state while roles are loading
  if (rolesLoading) {
    console.log('[ORG-ADMIN-PAGE] Showing loading state while roles load');
    return (
      <div className="container mx-auto px-4 lg:px-8 max-w-7xl">
        <div className="py-6 space-y-6 lg:py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading permissions...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!roles.isPlatformAdmin && !roles.isOrgAdmin) {
    console.log('[ORG-ADMIN-PAGE] Rendering null due to insufficient permissions');
    return null;
  }
  
  console.log('[ORG-ADMIN-PAGE] Rendering admin interface');
  return (
    <div className="container mx-auto px-4 lg:px-8 max-w-7xl">
      <div className="py-6 space-y-6 lg:py-8">
        <AdminPageHeader
          title="Organization Admin"
          description={`Manage ${organization?.display_name || 'your organization'} settings and members`}
          showOrganizationInfo={true}
        />

        {/* Navigation Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          {/* Mobile: Dropdown Navigation */}
          <div className="block sm:hidden">
            <Select value={activeTab} onValueChange={setActiveTab}>
              <SelectTrigger className="w-full h-12 text-left">
                <div className="flex items-center space-x-2">
                  {(() => {
                    const currentTab = orgTabs.find(tab => tab.value === activeTab);
                    const Icon = currentTab?.icon;
                    return (
                      <>
                        {Icon && <Icon className="h-4 w-4" />}
                        <span>{currentTab?.label}</span>
                      </>
                    );
                  })()}
                </div>
              </SelectTrigger>
              <SelectContent className="z-50">
                {orgTabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <SelectItem key={tab.value} value={tab.value}>
                      <div className="flex items-center space-x-2">
                        <Icon className="h-4 w-4" />
                        <span>{tab.label}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Desktop: Tab Navigation */}
          <div className="hidden sm:block">
            <ResponsiveTabsList className="grid-cols-3 sm:grid-cols-5">
              <ResponsiveTabsTrigger
                value="overview"
                icon={<BarChart3 className="h-4 w-4" />}
                label="Overview"
                mobileLabel="Stats"
              />
              <ResponsiveTabsTrigger
                value="members"
                icon={<Users className="h-4 w-4" />}
                label="Members"
              />
              <ResponsiveTabsTrigger
                value="pending"
                icon={<UserCheck className="h-4 w-4" />}
                label={`Pending${pendingCount > 0 ? ` (${pendingCount})` : ''}`}
                mobileLabel="Pending"
              />
              <ResponsiveTabsTrigger
                value="parcels"
                icon={<Package className="h-4 w-4" />}
                label="Parcel Types"
                mobileLabel="Parcels"
              />
              <ResponsiveTabsTrigger
                value="settings"
                icon={<Settings className="h-4 w-4" />}
                label="Settings"
              />
            </ResponsiveTabsList>
          </div>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <AnalyticsDashboard />
          </TabsContent>

          {/* Members Tab */}
          <TabsContent value="members" className="space-y-6">
            <MemberManagement />
          </TabsContent>

          {/* Pending Requests Tab */}
          <TabsContent value="pending" className="space-y-6">
            <PendingMembershipsManager />
          </TabsContent>

          {/* Parcel Types Tab */}
          <TabsContent value="parcels" className="space-y-6">
            <ParcelTypesErrorBoundary>
              <OrganizationParcelTypesManager />
            </ParcelTypesErrorBoundary>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <OrganizationSettings />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
export default OrganizationAdmin;